import Modal from '@material-ui/core/Modal';
import { useStyles } from './SelectTTMModal.styles.js';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import _ from 'lodash';
import moment from 'moment';
import 'react-jinke-music-player/assets/index.css';

import CircularProgress from '@material-ui/core/CircularProgress';
import { Collapse, Autocomplete, TextField, Chip, Popover, Select, MenuItem } from '@mui/material';

import LoopIcon from '@mui/icons-material/Loop';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  fetchSubscriptionInfo,
  uploadTTMInference,
  checkTTMInferenceCompletion,
  checkTTMInferenceCompletionById,
  getTTMInferenceSignedUrl,
  fetchTTMTags,
  fetchTTMInferenceChunk,
} from '../../services/page.services';

import { appActions } from '../../actions/app.actions.js';
import { userActions } from '../../actions/user.actions.js';

import HtmlTooltip from '../HtmlTooltip';
import tooltipImg from '../../img/tooltip.png';
import { labelLength, statusCheckInterval, creditsPerTtmInference } from '../../constants/app.constants.js';
import InfiniteScroll from 'react-infinite-scroll-component';
import { showMessageV2 } from '../../utils/page.utils.js';

export default function SelectTTMModal(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const tierToShowOutputFormat = ['PREMIUM', 'PRO', 'PREMIUM_YEARLY', 'PRO_YEARLY', 'ADMIN', 'CUSTOM'];
  const isAdmin = user?.subscription?.type === 'ADMIN';
  const isCustom = user?.subscription?.type === 'CUSTOM';

  const [fontStyle, setFontStyle] = useState('TheJamsil');

  const { open, loading } = props;

  const [alignment, setAlignment] = useState('generate');

  // generate tab
  const intervalIDRef = useRef(null);
  const [loadingText, setLoadingText] = useState('');
  const bottomRef = useRef(null); // ref to reference the bottom position
  const [outputFormat, setOutputFormat] = useState('mp3');

  const [tags, setTags] = useState([]);
  const [randomTags, setRandomTags] = useState([]);

  // does not require translations
  const lyricLabels = ['[Intro]', '[Verse]', '[Pre-Chorus]', '[Chorus]', '[Bridge]', '[Outro]'];

  const [promptMethod, setPromptMethod] = useState('prompt');
  const [lyricMethod, setLyricMethod] = useState('generate');
  const [prompt, setPrompt] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [lyricPrompt, setLyricPrompt] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('ko');
  const [customLyrics, setCustomLyrics] = useState('');
  const [label, setLabel] = useState('');

  const [ttmInferenceRemainingCount, setTtmInferenceRemainingcount] = useState();
  const [ttmInferenceInProgress, setTtmInferenceInProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [anchorEl, setAnchorEl] = useState();
  const lyricLabelOpen = Boolean(anchorEl);

  const [showOutputFormatTooltip, setShowOutputFormatTooltip] = useState(false);

  // history tab
  const [data, setData] = useState([]);
  const [query, setQuery] = useState('');

  const chunk = 50;
  const [chunkPage, setChunkPage] = useState(0);
  const [length, setLength] = useState(0);
  const controllerRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [selectedTtmInferenceId, setSelectedTtmInferenceId] = useState('');

  useEffect(() => {
    const defaultLanguage = localStorage.getItem('selectedLanguage') || navigator.language.split('-')[0];
    const defaultFont = defaultLanguage === 'ja' ? 'MPlus1' : 'TheJamsil';
    setFontStyle(defaultFont);
  }, []);

  // generate tab
  useEffect(() => () => intervalIDRef.current && clearInterval(intervalIDRef.current), []);

  useEffect(() => {
    if (userId) (async () => initPage())();
  }, [userId]);

  const hasStyle = (promptMethod === 'prompt' && prompt) || (promptMethod === 'tags' && selectedTags.length > 0);
  const hasLyrics = (lyricMethod === 'generate' && lyricPrompt) || (lyricMethod === 'custom' && customLyrics);

  const readyForSubmit = (() => {
    return (hasStyle || hasLyrics) && label;
  })();

  const emptyParams = !prompt && selectedTags.length === 0 && !lyricPrompt && !customLyrics;

  // when loadingtext exists, scroll to the very bottom
  useEffect(() => {
    if (loadingText !== '') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [loadingText]);

  useEffect(() => {
    if (!hasStyle && !hasLyrics && !label) {
      setCurrentStep(Math.max(1, currentStep));
    } else if (
      ((promptMethod === 'prompt' && prompt) ||
        (promptMethod === 'tags' && selectedTags.length > 0) ||
        (lyricMethod === 'generate' && lyricPrompt) ||
        (lyricMethod === 'custom' && customLyrics)) &&
      !label
    ) {
      setCurrentStep(Math.max(3, currentStep));
    }
  }, [promptMethod, prompt, selectedTags, lyricMethod, lyricPrompt, customLyrics, label]);

  const checkAndPollPendingInference = async () => {
    let ttmInferences = await checkTTMInferenceCompletion();

    while (_.some(ttmInferences, { status: 'PENDING' })) {
      setTtmInferenceInProgress(true);
      await new Promise(resolve => setTimeout(resolve, statusCheckInterval));
      ttmInferences = await checkTTMInferenceCompletion();
    }
    await checkRemainingCount();
    setTtmInferenceInProgress(false);
  };

  // shows 6 random tags from the 200 most popular tags
  const randomizeTags = async () => {
    if (tags.length > 0) {
      const popularTags = tags.slice(0, 200);
      const shuffled = [...popularTags].sort(() => 0.5 - Math.random());
      setRandomTags(shuffled.slice(0, 12));
    }
  };

  const initPage = async () => {
    try {
      if (!isAdmin && !isCustom) {
        checkAndPollPendingInference();
      }
      await checkRemainingCount();
      setTags(await fetchTTMTags());
    } catch (e) {
      showMessageV2(dispatch, t('modal.pageLoadFail'), { reloadOnClose: true });
    }
  };

  // randomize tags when the page loads
  useEffect(() => {
    if (tags.length > 0) {
      randomizeTags();
    }
  }, [tags]);

  useEffect(() => {
    setSelectedLanguage(t('languageDetector') || 'ko');
  }, [t('languageDetector')]);

  const checkRemainingCount = async () => {
    let subscriptionInfo = await fetchSubscriptionInfo();
    dispatch(userActions.updateSubscription(subscriptionInfo));
    setTtmInferenceRemainingcount(
      subscriptionInfo?.freeTtmInferenceRemaining + subscriptionInfo?.genCreditsRemaining || 0
    );
    return subscriptionInfo;
  };

  const checkCompleteForm = () => {
    let message = '';
    if (ttmInferenceInProgress) {
      message = t('ttmInference.modal.inProgress');
    } else if (ttmInferenceRemainingCount < creditsPerTtmInference) {
      message = t('ttmInference.modal.lackRemaining');
    } else if (emptyParams) {
      message = t('ttmInference.modal.emptyParams');
    } else if (label.trim().length === 0) {
      message = t('ttmInference.modal.noLabel');
    } else if (label.trim().length > labelLength.ttm) {
      message = t('ttmInference.modal.longLabel');
    }
    if (message) {
      showMessageV2(dispatch, message);
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    if (!checkCompleteForm()) return;
    setLoadingText(t('ttmInference.submit.uploading'));
    let inference;
    try {
      const usedPrompt = promptMethod === 'prompt' ? prompt : selectedTags;
      const lyrics = lyricMethod === 'generate' ? lyricPrompt : customLyrics;

      inference = await uploadTTMInference(
        label,
        usedPrompt,
        promptMethod,
        lyrics,
        lyricMethod,
        lyricMethod === 'generate' || (lyricMethod === 'custom' && lyrics.length === 0) ? selectedLanguage : null,
        false, // instrumental should be false
        outputFormat,
        null, // no model can be selected
        user.subscription.type
      );
    } catch (e) {
      showMessageV2(dispatch, t('ttmInference.submit.tryAgain'));
      console.log(e);
      setLoadingText('');
      return;
    }
    checkRemainingCount();
    setLoadingText(t('ttmInference.submit.inProgress'));

    if (inference) {
      const tick = async () => {
        try {
          const id = inference.id;
          const status = await checkTTMInferenceCompletionById(id);
          setLoadingText(t('ttmInference.submit.inProgress'));
          if (status === 'COMPLETE') {
            clearInterval(intervalIDRef.current);
            await checkRemainingCount();
            setLoadingText('');
            setAlignment('history');
          } else if (status === 'FAILED') {
            showMessageV2(dispatch, t('ttmInferenceTab.submit.tryAgain'));
            clearInterval(intervalIDRef.current);
            setLoadingText('');
          }
        } catch (e) {
          console.log(e);
        }
      };
      intervalIDRef.current = setInterval(tick, statusCheckInterval);
    }
  };

  const handleWavButtonClick = () => {
    if (!tierToShowOutputFormat.includes(user.subscription?.type)) {
      setShowOutputFormatTooltip(true);
    } else {
      setOutputFormat('wav');
      setShowOutputFormatTooltip(false);
    }
  };

  // history tab
  useEffect(() => {
    if (alignment === 'history') {
      const debounced = _.debounce(() => initData(false), 300);

      if (userId) {
        debounced();
      }

      return () => {
        debounced.cancel();
      };
    }
  }, [userId, alignment, query]);

  // add event listened to be triggered whenever the played song ends
  useEffect(() => {
    if (selectedAudio) {
      selectedAudio.addEventListener('ended', () => {
        setSelectedTtmInferenceId('');
        setPlaying(false);
      });
    }
    return () => {
      if (selectedAudio) {
        selectedAudio.pause();
        selectedAudio.removeEventListener('ended', () => {
          setSelectedTtmInferenceId('');
          setPlaying(false);
        });
      }
    };
  }, [selectedAudio]);

  useEffect(() => {
    if (selectedAudio) {
      playing ? selectedAudio.play() : selectedAudio.pause();
    }
  }, [playing]);

  const getBlobFromS3Url = async url => {
    const res = await fetch(url);
    return res.blob();
  };

  const handlePlay = async (ttmInferenceId, outputFormat) => {
    if (selectedTtmInferenceId === ttmInferenceId) {
      setPlaying(true);
    } else {
      setPlaying(false);
      const fileName = `${ttmInferenceId}.${outputFormat}`;
      const songUrl = await getTTMInferenceSignedUrl(ttmInferenceId, fileName);
      const songBlob = await getBlobFromS3Url(songUrl);
      if (songBlob.size > 2000) {
        const audio = new Audio(URL.createObjectURL(songBlob.slice(0, songBlob.size, `audio/mp3`)));
        setSelectedAudio(audio);
        setPlaying(true);
        setSelectedTtmInferenceId(ttmInferenceId);
      }
    }
  };

  const handlePlayClickDebounced = _.debounce(handlePlay, 500, { leading: true });

  const handlePause = async () => {
    setPlaying(false);
  };

  const initData = async scroll => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    controllerRef.current = new AbortController();
    const signal = controllerRef.current.signal;

    try {
      let songChunk;
      songChunk = await fetchTTMInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
      let songs = songChunk.rows;
      const incompleteList = _.filter(songs, {
        status: 'PENDING',
      });
      if (_.size(incompleteList) > 0) {
        await checkTTMInferenceCompletion(userId, incompleteList);
        songChunk = await fetchTTMInferenceChunk(chunk, scroll ? chunkPage : 0, query, signal);
        songs = songChunk.rows;
      }
      const cleanedData = songs.map(
        ({
          id,
          label,
          tags,
          prompt,
          lyricPrompt,
          customLyrics,
          generatedLyrics,
          instrumental,
          outputFormat,
          status,
          createdAt,
        }) => {
          return {
            id,
            label,
            tags,
            prompt,
            lyricPrompt,
            customLyrics,
            generatedLyrics,
            instrumental,
            outputFormat,
            status,
            createdAt: moment(createdAt).format('MM/DD/YY hh:mm A'),
          };
        }
      );

      if (scroll) {
        setData(prevData => [...prevData, ...cleanedData]);
        setChunkPage(prevChunkPage => prevChunkPage + 1);
      } else {
        setData(cleanedData);
        setChunkPage(1);
      }
      setLength(songChunk.count);
    } catch (err) {
      console.log(err);
      if (err.name != 'CanceledError') {
        showMessageV2(dispatch, t('historyTab.modal.error'), { reloadOnClose: true });
      }
    }
  };

  const [expandedCards, setExpandedCards] = useState([]);

  const handleExpand = index => {
    setExpandedCards(prevExpanded =>
      prevExpanded.includes(index) ? prevExpanded.filter(index => index !== index) : [...prevExpanded, index]
    );
  };

  const classes = useStyles();

  return (
    <Modal className={fontStyle} open={!!open} onClose={() => props.onSelectTTMModalClose()}>
      <div className={classes.container}>
        {!loading && (
          <>
            <div className={classes.header}>
              <div className={classes.subHeader}>
                <div className={classes.pageTitle}>
                  {alignment === 'generate'
                    ? t('ttmInference.selectModal.generate.title')
                    : t('ttmInference.selectModal.history.title')}
                </div>
                <HtmlTooltip
                  className={classes.compactTooltip}
                  title={
                    <div className={'globalTooltipContainer'}>
                      <div className={'globalTooltipTextContainer'}>
                        <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                        <div className={'globalTooltipText'}>{t('ttmInference.stepOne.tooltip.contents.0')}</div>
                      </div>
                      <div className={'globalTooltipTextContainer'}>
                        <div className="globalTooltipText globalTooltipTextBullet">&#x2022;</div>
                        <div className={'globalTooltipText'}>{t('ttmInference.stepOne.tooltip.contents.1')}</div>
                      </div>
                    </div>
                  }
                >
                  <img className={classes.tooltipImg} src={tooltipImg} alt="tooltip-img" />
                </HtmlTooltip>
              </div>
              <div
                className={classes.tabButton}
                onClick={() => setAlignment(alignment === 'generate' ? 'history' : 'generate')}
              >
                {alignment === 'generate' ? <HistoryIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                {alignment === 'generate'
                  ? t('ttmInference.selectModal.history.button')
                  : t('ttmInference.selectModal.generate.button')}
              </div>
            </div>
            <div className={classes.separater} />
            {alignment === 'generate' && (
              <>
                <div className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.promptMethodContainer}>
                      <div
                        className={`${classes.promptMethod} ${promptMethod === 'prompt' && classes.selectedPromptMethod}`}
                        onClick={() => setPromptMethod('prompt')}
                      >
                        {t('ttmInference.stepOne.promptMethod.prompt')}
                      </div>
                      <div
                        className={`${classes.promptMethod} ${promptMethod === 'tags' && classes.selectedPromptMethod}`}
                        onClick={() => setPromptMethod('tags')}
                      >
                        {t('ttmInference.stepOne.promptMethod.tags')}
                      </div>
                      <HtmlTooltip
                        className={classes.compactTooltip}
                        title={
                          <div className={'globalTooltipContainer'}>
                            <div className={'globalTooltipHeader'}>
                              {t('ttmInference.stepOne.promptMethod.tooltip.title')}
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.promptMethod.tooltip.contents.0')}
                              </div>
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.promptMethod.tooltip.contents.1')}
                              </div>
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.promptMethod.tooltip.contents.2')}
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <img
                          className={`${classes.tooltipImg} ${classes.noMargin}`}
                          src={tooltipImg}
                          alt="tooltip-img"
                        />
                      </HtmlTooltip>
                    </div>
                  </div>
                  {promptMethod === 'prompt' ? (
                    <input
                      className={classes.textInput}
                      placeholder={t(`ttmInference.stepOne.placeholder.prompt`)}
                      maxLength={1000}
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      disabled={loading}
                    />
                  ) : (
                    <>
                      <Autocomplete
                        multiple
                        size="small"
                        className={classes.autocomplete}
                        // need to override list style with a separate class
                        slotProps={{
                          paper: { className: classes.autocompletePaper },
                          listbox: { className: classes.autocompleteList },
                        }}
                        options={tags}
                        noOptionsText={t('ttmInference.stepOne.noOptions')}
                        value={selectedTags.map(
                          tagName => tags.find(tag => tag.name === tagName) || { name: tagName, korean: '' }
                        )}
                        onChange={(_, options) => {
                          setSelectedTags(options.map(option => option.name));
                        }}
                        getOptionLabel={option => option.name}
                        renderInput={params => {
                          return <TextField {...params} placeholder={t('ttmInference.stepOne.placeholder.tags')} />;
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.id}>
                              {t('languageDetector') === 'ko' ? option.korean : option.name}
                            </li>
                          );
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={option.id}
                                label={t('languageDetector') === 'ko' ? option.korean : option.name}
                                size="small"
                                {...tagProps}
                              />
                            );
                          })
                        }
                      />
                      <div className={classes.randomTagsOuterContainer}>
                        <LoopIcon onClick={() => randomizeTags()} className={classes.randomizeIcon} />
                        <div className={classes.randomTagsContainer}>
                          {randomTags.map((tag, index) => (
                            <div
                              key={index}
                              className={`${classes.tag} ${selectedTags.includes(tag.name) && classes.disabled}`}
                              onClick={() => {
                                !selectedTags.includes(tag.name) && setSelectedTags(prev => [...prev, tag.name]);
                              }}
                            >
                              {t('languageDetector') === 'ko' ? tag.korean : tag.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className={classes.section}>
                  <div className={classes.sectionHeader}>
                    <div className={classes.promptMethodContainer}>
                      <div
                        className={`${classes.promptMethod} ${lyricMethod === 'generate' && classes.selectedPromptMethod}`}
                        onClick={() => setLyricMethod('generate')}
                      >
                        {t('ttmInference.stepOne.lyricMethod.generate')}
                      </div>
                      <div
                        className={`${classes.promptMethod} ${lyricMethod === 'custom' && classes.selectedPromptMethod}`}
                        onClick={() => setLyricMethod('custom')}
                      >
                        {t('ttmInference.stepOne.lyricMethod.custom')}
                      </div>
                      <HtmlTooltip
                        className={classes.compactTooltip}
                        title={
                          <div className={'globalTooltipContainer'}>
                            <div className={'globalTooltipHeader'}>
                              {t('ttmInference.stepOne.lyricMethod.tooltip.title')}
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.lyricMethod.tooltip.contents.0')}
                              </div>
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.lyricMethod.tooltip.contents.1')}
                              </div>
                            </div>
                            <div className={'globalTooltipTextContainer'}>
                              <div className={'globalTooltipText'}>
                                {t('ttmInference.stepOne.lyricMethod.tooltip.contents.2')}
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <img
                          className={`${classes.tooltipImg} ${classes.noMargin}`}
                          src={tooltipImg}
                          alt="tooltip-img"
                        />
                      </HtmlTooltip>
                    </div>
                    {lyricMethod === 'generate' && (
                      <Select
                        value={selectedLanguage}
                        onChange={e => setSelectedLanguage(e.target.value)}
                        className={classes.languageSelect}
                        MenuProps={{
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                          },
                          slotProps: {
                            paper: {
                              sx: {
                                marginTop: '6px!important',
                                border: '1px solid #404040!important',
                                '& .MuiList-root': {
                                  '& .MuiMenuItem-root': {
                                    padding: '0 12px!important',
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value="en" dense>
                          EN
                        </MenuItem>
                        <MenuItem value="ko" dense>
                          한국어
                        </MenuItem>
                        <MenuItem value="ja" dense>
                          日本語
                        </MenuItem>
                        <MenuItem value="th" dense>
                          ภาษาไทย
                        </MenuItem>
                      </Select>
                    )}
                  </div>
                  {lyricMethod === 'generate' ? (
                    <input
                      className={classes.textInput}
                      placeholder={t('ttmInference.stepOne.placeholder.generateLyrics')}
                      maxLength={1000}
                      value={lyricPrompt}
                      onChange={e => setLyricPrompt(e.target.value)}
                      disabled={loading}
                    />
                  ) : (
                    <div className={classes.textAreaContainer}>
                      <textarea
                        className={classes.textArea}
                        placeholder={t('ttmInference.stepOne.placeholder.customLyrics')}
                        maxLength={3000}
                        rows={6}
                        value={customLyrics}
                        onChange={e => setCustomLyrics(e.target.value)}
                        disabled={loading}
                      />
                      <div className={classes.textAreaFooter}>
                        <div className={classes.textAreaCount}>{customLyrics.length} / 3000</div>
                        <div className={classes.lyricLabelButton} onClick={e => setAnchorEl(e.currentTarget)}>
                          <PlaylistAddIcon className={classes.lyricLabelIcon} />
                        </div>
                      </div>
                      <Popover
                        open={lyricLabelOpen}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        className={classes.popover}
                        elevation={0}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        disableScrollLock
                      >
                        <div className={classes.lyricLabelContainer}>
                          {lyricLabels.map((label, index) => (
                            <div
                              key={index}
                              className={classes.lyricLabel}
                              onClick={() => {
                                const labelText = `${label}\n\n`;
                                setCustomLyrics(prev => prev + labelText);
                              }}
                            >
                              {label}
                            </div>
                          ))}
                        </div>
                      </Popover>
                    </div>
                  )}
                </div>
                <div className={classes.stepHeader}>
                  <span>{t('ttmInference.stepThree.title')}</span>
                </div>
                <div className={`${classes.section} ${classes.noMargin}`}>
                  <input
                    onChange={e => setLabel(e.target.value)}
                    placeholder={t('ttmInference.stepThree.placeholder')}
                    disabled={loading}
                    className={classes.textInput}
                    value={label}
                  />
                  <div className={classes.outputButtonContainer}>
                    <div
                      className={`${classes.extensionButton} ${outputFormat == 'mp3' ? classes.extensionSelected : ''}`}
                      onClick={() => setOutputFormat('mp3')}
                    >
                      {'MP3'}
                    </div>
                    <HtmlTooltip
                      title={
                        <div className={'globalTooltipContainer'}>
                          <div className={'globalTooltipTextContainer'}>
                            <div className={'globalTooltipText'}>
                              {t('ttmInference.stepThree.tooltip.outputFormatMessage')}
                            </div>
                          </div>
                          <div className={'globalTooltipTextContainer'}>
                            <Link to="/subscription" className={'globalTooltipTextLink'}>
                              {user.subscription?.type.startsWith('BASIC')
                                ? t('ttmInference.stepThree.tooltip.upgradePrompt')
                                : t('ttmInference.stepThree.tooltip.subscribePrompt')}
                            </Link>
                          </div>
                        </div>
                      }
                      interactive
                      leaveDelay={200}
                      open={showOutputFormatTooltip}
                      onClose={() => setShowOutputFormatTooltip(false)}
                    >
                      <div
                        className={`${classes.extensionButton} ${outputFormat === 'wav' ? classes.extensionSelected : ''}`}
                        onClick={handleWavButtonClick}
                      >
                        {'WAV'}
                      </div>
                    </HtmlTooltip>
                  </div>
                </div>
                <div className={classes.separater} />
                <div className={classes.submitContainer}>
                  {loadingText ? (
                    <div className={classes.loadingContainer}>
                      <div>
                        <CircularProgress size="1.4rem" />
                      </div>
                      <div className={classes.loadingText}>{loadingText}</div>
                    </div>
                  ) : (
                    <>
                      <div className={classes.submitInfo}>
                        <div className={classes.creditUsageText}>
                          {creditsPerTtmInference} {t('ttmInference.submit.creditUsage')}
                        </div>
                        <div>{t('ttmInference.submit.info.0')}</div>
                      </div>
                      {ttmInferenceInProgress ? (
                        <div className={classes.progressButton}>
                          <>
                            <div>{t('ttmInference.submit.alreadyInProgress')}</div>
                            <CircularProgress className={classes.loadingCircle} />
                          </>
                        </div>
                      ) : (
                        <div
                          className={`${classes.submitButton} ${!readyForSubmit && classes.disabledButton}`}
                          onClick={handleSubmit}
                        >
                          {userId && ttmInferenceRemainingCount < 2
                            ? t('ttmInference.submit.lackRemaining')
                            : t('ttmInference.submit.start')}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div ref={bottomRef} />
              </>
            )}
            {alignment === 'history' && (
              <>
                <input
                  className={classes.textInput}
                  placeholder={t(`ttmInference.selectModal.history.searchbar`)}
                  maxLength={1000}
                  value={query}
                  onChange={query => setQuery(query.target.value)}
                  disabled={loading}
                />
                <InfiniteScroll
                  className={classes.infiniteScroll}
                  dataLength={data.length}
                  next={() => initData(true)}
                  hasMore={data.length < length}
                  loader={<div className={classes.pagination}>{t('historyTab.pagination.loader')}</div>}
                >
                  {data
                    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                    .map(
                      (
                        {
                          id,
                          label,
                          tags,
                          prompt,
                          lyricPrompt,
                          customLyrics,
                          generatedLyrics,
                          instrumental,
                          outputFormat,
                          status,
                          createdAt,
                        },
                        index
                      ) => {
                        return (
                          !instrumental && (
                            <div
                              className={`${classes.card} ${status === 'COMPLETE' && classes.clickableCard}`}
                              onClick={() => {
                                if (status === 'COMPLETE') {
                                  props.onSelectTTM({ id, label, outputFormat });
                                }
                              }}
                            >
                              <div className={classes.cardContent}>
                                <div className={classes.cardInfo}>
                                  <div className={classes.cardLabel}>{label}</div>
                                  <div className={classes.cardTimestamp}>{createdAt}</div>
                                </div>
                                <div className={classes.cardAction}>
                                  <div className={classes.cardStatus}>
                                    {status === 'FAILED' && t('historyTab.failed')}
                                    {status === 'PENDING' && t('historyTab.inProgress')}
                                    {status === 'COMPLETE' && (
                                      <>
                                        {playing && id === selectedTtmInferenceId ? (
                                          <PauseCircleOutlineIcon
                                            className={classes.audioIcon}
                                            onClick={event => {
                                              event.stopPropagation();
                                              handlePause();
                                            }}
                                          />
                                        ) : (
                                          <PlayCircleOutlineIcon
                                            className={classes.audioIcon}
                                            onClick={event => {
                                              event.stopPropagation();
                                              handlePlayClickDebounced(id, outputFormat);
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </div>
                                  {status === 'COMPLETE' && (
                                    <ExpandMoreIcon
                                      className={classes.expandIcon}
                                      onClick={event => {
                                        event.stopPropagation();
                                        handleExpand(index);
                                      }}
                                      sx={
                                        expandedCards.includes(index) && {
                                          transform: 'rotate(180deg)',
                                        }
                                      }
                                    />
                                  )}
                                </div>
                              </div>
                              <Collapse in={expandedCards.includes(index)} timeout="auto" unmountOnExit>
                                <div className={classes.cardExpand}>
                                  {tags && (
                                    <div className={classes.tagsSection}>
                                      <div className={classes.tagsTitle}>{t('historyTab.ttmInfo.tags')}:</div>
                                      {tags?.map((tag, index) => (
                                        <span key={index} className={classes.tag}>
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {prompt && (
                                    <div>
                                      <div className={classes.sectionTitle}>{t('historyTab.ttmInfo.prompt')}:</div>
                                      <div className={classes.sectionText}>{prompt}</div>
                                    </div>
                                  )}
                                  {lyricPrompt && (
                                    <div>
                                      <div className={classes.sectionTitle}>{t('historyTab.ttmInfo.lyricPrompt')}:</div>
                                      <div className={classes.sectionText}>{lyricPrompt}</div>
                                    </div>
                                  )}
                                  {generatedLyrics && (
                                    <div>
                                      <div className={classes.lyricsContainer}>{JSON.parse(generatedLyrics)}</div>
                                    </div>
                                  )}
                                  {customLyrics && (
                                    <div className={classes.lyricsContainer}>{JSON.parse(customLyrics)}</div>
                                  )}
                                </div>
                              </Collapse>
                            </div>
                          )
                        );
                      }
                    )}
                </InfiniteScroll>
              </>
            )}
          </>
        )}
        {loading && <CircularProgress className={classes.loading} />}
      </div>
    </Modal>
  );
}
